import React, { useState, useEffect } from 'react'
import { apiClient } from '@/utils/api'
import toast from 'react-hot-toast'
import {
  Monitor, Settings, Moon, Sun, Palette, Grid3X3, Clock, CheckSquare,
  Trophy, Calendar, Cloud, Home, Music, Camera, Utensils, ChevronDown,
  ChevronUp, Save, RefreshCw, Smartphone, Tablet, Tv, Wifi, WifiOff
} from 'lucide-react'

// A platform pairing from GET /api/kiosk/devices (Phoenix 6.0.160+).
interface KioskDevice {
  id: string
  device_name: string
  device_type: string
  status: string
  is_active: boolean
  approved_at?: string
  created_at: string
}

interface WidgetConfig {
  id: string
  name: string
  icon: React.ReactNode
  description: string
  enabled: boolean
  category: 'core' | 'calendar' | 'smart_home' | 'entertainment'
}

const WIDGET_DEFINITIONS: Omit<WidgetConfig, 'enabled'>[] = [
  { id: 'chores', name: 'Chores', icon: <CheckSquare className="w-5 h-5" />, description: 'Today\'s chores and pending tasks', category: 'core' },
  { id: 'leaderboard', name: 'Leaderboard', icon: <Trophy className="w-5 h-5" />, description: 'Points ranking and streaks', category: 'core' },
  { id: 'clock', name: 'Clock', icon: <Clock className="w-5 h-5" />, description: 'Current time display', category: 'core' },
  { id: 'calendar', name: 'Calendar', icon: <Calendar className="w-5 h-5" />, description: 'Family and school events', category: 'calendar' },
  { id: 'weather', name: 'Weather', icon: <Cloud className="w-5 h-5" />, description: 'Local weather forecast', category: 'calendar' },
  { id: 'dinner', name: 'Dinner Menu', icon: <Utensils className="w-5 h-5" />, description: 'Tonight\'s dinner and attendance', category: 'core' },
  { id: 'home_assistant', name: 'Smart Home', icon: <Home className="w-5 h-5" />, description: 'Lights, switches, and sensors', category: 'smart_home' },
  { id: 'cameras', name: 'Cameras', icon: <Camera className="w-5 h-5" />, description: 'Security camera feeds', category: 'smart_home' },
  { id: 'music', name: 'Music Controls', icon: <Music className="w-5 h-5" />, description: 'Spotify and media controls', category: 'entertainment' },
]

const THEMES = [
  { id: 'light', name: 'Light', icon: <Sun className="w-5 h-5" /> },
  { id: 'dark', name: 'Dark', icon: <Moon className="w-5 h-5" /> },
  { id: 'auto', name: 'Auto', icon: <Palette className="w-5 h-5" /> },
]

const LAYOUTS = [
  { id: 'grid', name: 'Grid', description: 'Fixed grid layout' },
  { id: 'flex', name: 'Flexible', description: 'Auto-adjusting layout' },
  { id: 'compact', name: 'Compact', description: 'Space-efficient layout' },
]

export default function KioskSettings() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [devices, setDevices] = useState<KioskDevice[]>([])
  const [widgets, setWidgets] = useState<WidgetConfig[]>([])
  const [theme, setTheme] = useState('dark')
  const [layout, setLayout] = useState('grid')
  const [refreshInterval, setRefreshInterval] = useState(60)
  const [expandedCategory, setExpandedCategory] = useState<string | null>('core')

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    setLoading(true)
    try {
      // Load devices
      const devicesResponse = await apiClient.get('/api/kiosk/devices').catch(() => ({ devices: [] }))
      setDevices(devicesResponse?.devices || devicesResponse || [])

      // Load kiosk config from family settings
      const configResponse = await apiClient.get('/api/kiosk/dashboard-config').catch(() => ({}))

      // Initialize widgets with defaults, override with saved settings
      const savedWidgets = configResponse?.widgets || {}
      const initializedWidgets = WIDGET_DEFINITIONS.map(w => ({
        ...w,
        enabled: savedWidgets[w.id]?.enabled ?? ['chores', 'leaderboard', 'clock', 'dinner'].includes(w.id)
      }))
      setWidgets(initializedWidgets)

      // Set other configs
      if (configResponse?.theme) setTheme(configResponse.theme)
      if (configResponse?.layout) setLayout(configResponse.layout)
      if (configResponse?.refresh_interval) setRefreshInterval(configResponse.refresh_interval)

    } catch (error) {
      console.error('Failed to load kiosk settings:', error)
      // Initialize with defaults
      setWidgets(WIDGET_DEFINITIONS.map(w => ({
        ...w,
        enabled: ['chores', 'leaderboard', 'clock', 'dinner'].includes(w.id)
      })))
    } finally {
      setLoading(false)
    }
  }

  const toggleWidget = (widgetId: string) => {
    setWidgets(prev => prev.map(w =>
      w.id === widgetId ? { ...w, enabled: !w.enabled } : w
    ))
  }

  const saveSettings = async () => {
    setSaving(true)
    try {
      // Build config object
      const config = {
        theme,
        layout,
        refresh_interval: refreshInterval,
        widgets: widgets.reduce((acc, w) => ({
          ...acc,
          [w.id]: { enabled: w.enabled }
        }), {})
      }

      // Save to family settings
      await apiClient.put('/api/family/settings', {
        kiosk_config: config
      })

      toast.success('Kiosk settings saved')
    } catch (error) {
      console.error('Failed to save kiosk settings:', error)
      toast.error('Failed to save settings')
    } finally {
      setSaving(false)
    }
  }

  const getDeviceIcon = (type: string) => {
    switch (type) {
      case 'tv': return <Tv className="w-5 h-5" />
      case 'tablet': return <Tablet className="w-5 h-5" />
      default: return <Smartphone className="w-5 h-5" />
    }
  }

  const getDeviceStatus = (device: KioskDevice) => {
    if (device.status === 'revoked') return { icon: <WifiOff className="w-4 h-4" />, color: 'text-red-500', label: 'Revoked' }
    if (device.status === 'active') return { icon: <Wifi className="w-4 h-4" />, color: 'text-green-500', label: 'Active' }
    return { icon: <Wifi className="w-4 h-4" />, color: 'text-yellow-500', label: 'Paired' }
  }

  const categories = [
    { id: 'core', name: 'Core Widgets', description: 'Essential kiosk features' },
    { id: 'calendar', name: 'Calendar & Weather', description: 'Schedule and weather info' },
    { id: 'smart_home', name: 'Smart Home', description: 'Home automation controls' },
    { id: 'entertainment', name: 'Entertainment', description: 'Music and media' },
  ]

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Monitor className="w-6 h-6 text-blue-600" />
          <h2 className="text-xl font-semibold text-gray-900">Kiosk Display Settings</h2>
        </div>
        <button
          onClick={saveSettings}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Changes
        </button>
      </div>

      {/* Paired Devices */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="font-medium text-gray-900 mb-3">Paired Devices</h3>
        {devices.length === 0 ? (
          <p className="text-gray-500 text-sm">
            No kiosk devices paired yet. Go to Settings &gt; Devices to pair a kiosk.
          </p>
        ) : (
          <div className="space-y-2">
            {devices.map(device => {
              const status = getDeviceStatus(device)
              return (
                <div key={device.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    {getDeviceIcon(device.device_type)}
                    <div>
                      <p className="font-medium text-gray-900">{device.device_name}</p>
                    </div>
                  </div>
                  <div className={`flex items-center gap-1 ${status.color}`}>
                    {status.icon}
                    <span className="text-sm">{status.label}</span>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Theme and Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Theme */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="font-medium text-gray-900 mb-3">Theme</h3>
          <div className="grid grid-cols-3 gap-2">
            {THEMES.map(t => (
              <button
                key={t.id}
                onClick={() => setTheme(t.id)}
                className={`flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-colors ${
                  theme === t.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {t.icon}
                <span className="text-sm font-medium">{t.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Layout */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="font-medium text-gray-900 mb-3">Layout</h3>
          <div className="space-y-2">
            {LAYOUTS.map(l => (
              <button
                key={l.id}
                onClick={() => setLayout(l.id)}
                className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-colors ${
                  layout === l.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Grid3X3 className="w-5 h-5" />
                <div className="text-left">
                  <p className="font-medium text-sm">{l.name}</p>
                  <p className="text-xs text-gray-500">{l.description}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Refresh Interval */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="font-medium text-gray-900 mb-3">Refresh Interval</h3>
        <div className="flex items-center gap-4">
          <input
            type="range"
            min="15"
            max="300"
            step="15"
            value={refreshInterval}
            onChange={(e) => setRefreshInterval(Number(e.target.value))}
            className="flex-1"
          />
          <span className="text-sm font-medium text-gray-700 w-24 text-right">
            {refreshInterval < 60 ? `${refreshInterval} sec` : `${Math.round(refreshInterval / 60)} min`}
          </span>
        </div>
        <p className="text-xs text-gray-500 mt-2">How often the kiosk refreshes data</p>
      </div>

      {/* Widgets */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h3 className="font-medium text-gray-900">Widgets</h3>
          <p className="text-sm text-gray-500">Choose which widgets appear on your kiosk</p>
        </div>

        {categories.map(category => {
          const categoryWidgets = widgets.filter(w => w.category === category.id)
          const isExpanded = expandedCategory === category.id
          const enabledCount = categoryWidgets.filter(w => w.enabled).length

          return (
            <div key={category.id} className="border-b border-gray-200 last:border-b-0">
              <button
                onClick={() => setExpandedCategory(isExpanded ? null : category.id)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
              >
                <div>
                  <p className="font-medium text-gray-900">{category.name}</p>
                  <p className="text-sm text-gray-500">{category.description}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-500">
                    {enabledCount}/{categoryWidgets.length} enabled
                  </span>
                  {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </div>
              </button>

              {isExpanded && (
                <div className="p-4 pt-0 space-y-2">
                  {categoryWidgets.map(widget => (
                    <div
                      key={widget.id}
                      onClick={() => toggleWidget(widget.id)}
                      className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                        widget.enabled ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 border border-gray-200'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={widget.enabled ? 'text-blue-600' : 'text-gray-400'}>
                          {widget.icon}
                        </div>
                        <div>
                          <p className={`font-medium ${widget.enabled ? 'text-gray-900' : 'text-gray-500'}`}>
                            {widget.name}
                          </p>
                          <p className="text-xs text-gray-500">{widget.description}</p>
                        </div>
                      </div>
                      <div className={`w-10 h-6 rounded-full p-1 transition-colors ${
                        widget.enabled ? 'bg-blue-600' : 'bg-gray-300'
                      }`}>
                        <div className={`w-4 h-4 rounded-full bg-white transition-transform ${
                          widget.enabled ? 'translate-x-4' : 'translate-x-0'
                        }`} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> Changes will take effect the next time your kiosk refreshes.
          You can force a refresh by pressing the refresh button on your kiosk device.
        </p>
      </div>
    </div>
  )
}
